import { FC, ReactNode, ComponentType, isValidElement } from 'react';
import { Card } from '../ui/Card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface MetricTrend {
  value: string;
  positive?: boolean | null; // true = up/green, false = down/red, null = neutral
  label?: string;
}

export interface MetricWidgetProps {
  label?: string;
  title?: string;
  value: string | number | ReactNode;
  description?: string | ReactNode;
  icon?: ComponentType<{ className?: string }> | ReactNode;
  trend?: MetricTrend;
  badge?: ReactNode;
  className?: string;
}

export const MetricWidget: FC<MetricWidgetProps> = ({
  label,
  title,
  value,
  description,
  icon,
  trend,
  badge,
  className = '',
}) => {
  const resolvedLabel = label || title || '';
  const renderIcon = () => {
    if (!icon) return null;
    if (isValidElement(icon)) {
      return icon;
    }
    const IconComponent = icon as ComponentType<{ className?: string }>;
    return <IconComponent className="h-4 w-4 text-content-muted" />;
  };

  return (
    <Card
      className={`hover:border-surface-border/80 transition-all ${className}`}
    >
      <Card.Body className="space-y-2 p-4 sm:p-5">
        {/* Header do Widget: Rótulo e Ícone/Badge */}
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-micro font-medium text-content-muted sm:text-xs">
            {resolvedLabel}
          </span>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            {badge}
            {icon && (
              <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle">
                {renderIcon()}
              </div>
            )}
          </div>
        </div>

        {/* Valor de Destaque */}
        <div className="flex items-baseline gap-2">
          {typeof value === 'string' || typeof value === 'number' ? (
            <span className="font-mono text-xl font-black tracking-tight text-content-main sm:text-2xl">
              {value}
            </span>
          ) : (
            value
          )}
        </div>

        {/* Rodapé: Tendência e/ou Descrição sutil */}
        {(trend || description) && (
          <div className="flex flex-wrap items-center gap-2 pt-0.5 text-micro text-content-muted">
            {trend && (
              <span
                className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-2xs font-semibold ${
                  trend.positive === true
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : trend.positive === false
                      ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                      : 'bg-surface-subtle text-content-muted'
                } `}
              >
                {trend.positive === true ? (
                  <TrendingUp className="h-3 w-3" />
                ) : trend.positive === false ? (
                  <TrendingDown className="h-3 w-3" />
                ) : (
                  <Minus className="h-3 w-3" />
                )}
                <span>{trend.value}</span>
              </span>
            )}
            {trend?.label && <span>{trend.label}</span>}
            {description && <span>{description}</span>}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
